<template>
	<div class="org-reports-container">
		<div class="org-reports-header">
			<h1 v-t="'admin_overview_reports_title'" />

			<Button
				primary
				:loading="showLoading"
				@click="generateReport()"
				class="generate"
				>{{ $t('admin_reports_generate_button') }}</Button
			>
		</div>

		<div class="org-reports-description-text">
			{{ $t('admin_reports_explanation_heading') }}
		</div>

		<Table
			:columns="columns"
			:rows="reports"
			:loadingMore="loadingMore"
			:loadingOverlay="showLoading"
			infinite
			loadMoreTrigger="0px"
			emptyState="No reports in history"
			@load-more="onLoadMore"
			ref="table"
			height="calc(100vh - 228px)"
		>
			<template #cell-created_at="{ data }">
				{{ formattedDate(data.created_at) }}
			</template>
			<template #cell-requester="{ data }">
				{{ data.requester.fullname }}
			</template>
			<template #cell-status="{ data }">
				<Button
					class="generating-btn"
					small
					v-if="data.status === 'NEW' || data.status === 'PENDING'"
				>
					{{ $t('admin_reports_status_generating') }}
				</Button>
				<a
					v-if="data.status === 'COMPLETE'"
					:href="store.mediaPath(data)"
					download=""
					target="_blank"
				>
					<Button primary small>{{
						$t('admin_reports_status_download')
					}}</Button>
				</a>
				<Button class="failed-btn" danger small v-if="data.status === 'FAILED'">
					{{ $t('admin_reports_status_failed') }}
				</Button>
			</template>
			<template #cell-delete="{ data }">
				<TrashCanIcon
					v-if="data.status === 'COMPLETE' || data.status === 'FAILED'"
					class="delete-icon danger"
					@click="prepDelete(data.id)"
				/>
			</template>
		</Table>

		<StandardModal
			:title="$t('admin_reports_deletemodal_title')"
			v-if="reportToDelete"
			open
			small
			@cancel="reportToDelete = false"
			class="reports-modal"
		>
			<div class="delete-modal-wrapper">
				<div class="delete-content">
					<img
						src="/images/common/red-trash-can.svg"
						class="delete-halo"
						alt="delete icon"
					/>
					<p class="delete-text">
						{{ $t('admin_reports_deletemodal_confirm_text') }}
					</p>
				</div>
			</div>
			<template #footer-right>
				<Button @click="reportToDelete = false">
					{{ $t('common_button_cancel') }}
				</Button>
				<Button danger @click="deleteReport" :loading="isDeleting">
					{{ $t('common_button_delete') }}
				</Button>
			</template>
		</StandardModal>
	</div>
</template>

<script>
import store from '@/store'
import { post } from '@/helpers/api'
import { StandardModal, Table, Button } from '@/components/common'
import moment from 'moment'
import { TrashCanIcon } from '@/icons'
import { getErrorText } from '@/helperMethods'

export default {
	components: {
		StandardModal,
		Table,
		TrashCanIcon,
		Button,
	},
	data() {
		return {
			store,
			reports: [],
			showLoading: true,
			loadingMore: false,
			requestID: null,
			currentPage: 1,
			hasMore: false,
			reportToDelete: null,
			isGenerating: false,
			columns: [
				{
					field: 'created_at',
					label: this.$t('admin_reports_table_heading_date'),
				},
				{
					field: 'requester',
					label: this.$t('admin_reports_table_heading_requestedby'),
				},
				{
					field: 'status',
					label: this.$t('admin_reports_table_heading_status'),
					width: '400px',
					align: 'center',
				},
				{
					field: 'delete',
					width: '100px',
				},
			],
			isDeleting: false,
		}
	},

	mounted() {
		this.getReportList()
	},

	methods: {
		generateReport() {
			if (this.isGenerating) {
				this.$alert.error(this.$t('admin_reports_duplicate_report_warning'))
				return
			}

			this.showLoading = true

			let post_data = {
				type: 'GROUP_STATS',
				organization_id: this.store.auth.user.organizations[0].id,
			}

			post('/api/v1/report.create', post_data)
				.then(({ data }) => {
					this.reports.unshift(data.report)
					setTimeout(() => {
						this.getReportInfo(data.report.id)
					}, 5000)
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t(getErrorText(error.response.data.error)))
				})
				.finally(() => {
					this.showLoading = false
				})
		},

		getReportList() {
			let current_id = this.uniqueID()
			this.requestID = current_id

			if (this.currentPage > 1) {
				this.loadingMore = true
			}

			let post_data = {
				type: 'GROUP_STATS',
				organization_id: this.store.auth.user.organizations[0].id,
				page: this.currentPage,
				itemsPerPage: 10,
				orderBy: ['updated_at', 'desc'],
			}

			post('/api/v1/report.list', post_data)
				.then(({ data }) => {
					if (current_id !== this.requestID) return
					if (this.currentPage == 1) {
						this.reports = data.reports
						this.$refs.table.scrollToTop()
					} else {
						this.reports = this.reports.concat(data.reports)
					}

					this.currentPage = this.currentPage + 1
					this.hasMore = data.reports.length === data.pagesize

					this.isGenerating = data.reports.some(function (report) {
						return report.status === 'NEW' || report.status === 'PENDING'
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t(getErrorText(e.response.data.error)))
				})
				.finally(() => {
					this.showLoading = false
					this.loadingMore = false
				})
		},

		getReportInfo(id) {
			post('/api/v1/report.info', { report_id: id })
				.then(({ data }) => {
					let position = this.reports.findIndex((x) => x.id === data.report.id)
					this.reports[position] = data.report

					if (
						data.report.status === 'NEW' ||
						data.report.status === 'PENDING'
					) {
						setTimeout(() => {
							this.getReportInfo(data.report.id)
						}, 5000)
					} else {
						this.isGenerating = false
					}
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t(getErrorText(e.response.data.error)))
				})
		},

		deleteReport() {
			this.isDeleting = true
			post('/api/v1/report.delete', { report_id: this.reportToDelete })
				.then(() => {
					this.reports = this.reports.filter((report) => {
						return report.id != this.reportToDelete
					})
					this.reportToDelete = null
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(this.$t(getErrorText(e.response.data.error)))
				})
				.finally(() => {
					this.isDeleting = false
				})
		},

		formattedDate(date) {
			return moment(date + 'Z').format('lll')
		},

		uniqueID() {
			return '_' + Math.random().toString(36).substr(2, 9)
		},

		prepDelete(id) {
			this.reportToDelete = id
		},

		onLoadMore() {
			if (this.hasMore) {
				this.getReportList()
			}
		},
	},
}
</script>
<style lang="scss" scoped>
@import 'src/sass/variables';

.generating-btn {
	cursor: not-allowed !important;
	&:hover {
		background: white !important;
	}
}
.failed-btn {
	cursor: not-allowed !important;
	&:hover {
		background: $error-red !important;
	}
}

.org-reports-header {
	height: 105px;
	display: flex;
	align-items: center;
	padding: 0 32px;
	@include flex-spread;
	h1 {
		font-family: Avenir-Heavy;
		font-size: 36px;
		margin: 0;
		color: var(--neutral-800);
	}
	border-bottom: 1px solid var(--neutral-200);
}

.org-reports-header {
	background: #fafafa;
}

.back-btn-bg {
	font-family: Avenir-Heavy;
	font-size: 20px;
	color: #4f5053;
	padding-top: 5px;
	margin-left: 14px;
}

.org-reports-description-text {
	padding: 24px;
	font-family: Avenir-Medium;
	font-size: 14px;
	font-weight: normal;
	color: #9498a3;
}

.org-reports-history {
	font-family: Avenir-Medium;
	font-size: 20px;
	color: #4f5053;
	margin-top: 20px;
	margin-bottom: 28px;
}

.delete-modal-wrapper {
	height: 100%;
	@include flex-center;
	.delete-content {
		@include flex-column;
		align-items: center;
		.delete-halo {
			width: 130px;
			height: 130px;
		}
		.delete-text {
			margin-top: 40px;
			text-align: center;
			max-width: 400px;
		}
	}
}
.delete-icon {
	cursor: pointer;
	font-size: 24px;
	color: var(--red-200);
}

a {
	text-decoration: none;
}
</style>
<style lang="scss">
.reports-modal {
	.table-header {
		border-top: none !important;
	}
}
</style>
